import api from '@/api';
import type { Venue, CreateVenueRequest, UpdateVenueRequest, VenueListResponse, VenueListScope } from '../types/venue';

// Fetch paginated venues with filters
export async function fetchVenues({
    page = 1,
    limit = 10,
    status,
    search,
    city,
    lat,
    lng,
    radius,
    scope,
    organizerId,
}: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    city?: string;
    lat?: number;
    lng?: number;
    radius?: number;
    scope?: VenueListScope;
    organizerId?: string;
}): Promise<VenueListResponse> {
    const params: any = { page, limit };
    if (status) params.status = status;
    if (search) params.search = search;
    if (city) params.city = city;
    if (lat) params.lat = lat;
    if (lng) params.lng = lng;
    if (radius) params.radius = radius;
    if (scope) params.scope = scope;
    if (organizerId) params.organizerId = organizerId;

    const res = await api.get('/eventopia/venues', { params });
    return res.data;
}

// Fetch single venue by ID
export async function fetchVenue(id: string): Promise<{ success: boolean; data: Venue }> {
    const res = await api.get(`/eventopia/venues/${id}`);
    return res.data;
}

export async function fetchEventVenues(eventId: string): Promise<{ success: boolean; data: Venue[]; activeVenueId: string | null }> {
    const res = await api.get(`/eventopia/venues/event/${eventId}`);
    return res.data;
}

// Create new venue
export async function createVenue(venueData: CreateVenueRequest): Promise<{ success: boolean; data: Venue; message: string }> {
    const res = await api.post('/eventopia/venues', venueData);
    return res.data;
}


// Update venue
export async function updateVenue(id: string, venueData: UpdateVenueRequest): Promise<{ success: boolean; data: Venue; message: string }> {
    const res = await api.put(`/eventopia/venues/${id}`, venueData);
    return res.data;
}

// Delete venue
export async function deleteVenue(id: string): Promise<{ success: boolean; message: string }> {
    const res = await api.delete(`/eventopia/venues/${id}`);
    return res.data;
}

// Upload seatmap file
export async function uploadSeatmap(id: string, file: File, confirmTierPriceRemoval = false): Promise<{ success: boolean; data: Venue; message: string }> {
    const formData = new FormData();
    formData.append('seatmap', file);

    const res = await api.post(`/eventopia/venues/${id}/seatmap`, formData, {
        params: confirmTierPriceRemoval ? { confirmTierPriceRemoval: 'true' } : undefined,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return res.data;
}

export interface OrganizerOption {
    _id: string;
    orgName: string;
    shortName?: string;
}

export async function fetchOrganizerDirectory(): Promise<OrganizerOption[]> {
    const res = await api.get('/eventopia/organizer/directory');
    return res.data?.data ?? [];
}

export async function cloneVenue(id: string, body: { keepTiering: boolean; requestId: string }): Promise<{ success: boolean; data: Venue; message: string }> {
    const res = await api.post(`/eventopia/venues/${id}/clone`, body);
    return res.data;
}
